import type { PaymentInstructionDraft, PaymentInstructionPreviewPayload } from '@/types/payment-instructions';
import { z } from 'zod';

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

const maxLengthMessage = (max: number) => `Use ${max} characters or fewer`;

export const paymentInstructionsFormSchema = z.object({
  amount: z
    .string()
    .trim()
    .min(1, 'Amount is required')
    .regex(AMOUNT_PATTERN, 'Enter a number with at most 2 decimal places')
    .refine(value => Number(value) > 0, 'Amount must be greater than 0'),
  reference: z.string().trim().min(1, 'Reference is required').max(200, maxLengthMessage(200)),
  deadline: z.string(),
  legal_deadline: z.string(),
  payment_details: z
    .array(
      z.object({
        label: z.string().trim().min(1, 'Label is required').max(100, maxLengthMessage(100)),
        value: z.string().trim().min(1, 'Value is required').max(500, maxLengthMessage(500)),
      })
    )
    .min(1, 'Add at least one payment detail'),
});

/**
 * The deadline may stay empty when the filing has a due date, because the API then defaults it. A filing with
 * no due date has no default, so the API refuses a send without one.
 */
export const buildPaymentInstructionsFormSchema = (requireDeadline: boolean) =>
  paymentInstructionsFormSchema.extend({
    deadline: requireDeadline
      ? z.string().min(1, 'Choose a payment deadline. This filing has no due date.')
      : z.string(),
  });

export type PaymentInstructionsFormData = z.infer<typeof paymentInstructionsFormSchema>;

export const createPaymentInstructionsDefaultValues = (
  draft: PaymentInstructionDraft
): PaymentInstructionsFormData => ({
  amount: draft.amount,
  reference: '',
  deadline: draft.deadline ?? '',
  legal_deadline: draft.legal_deadline ?? '',
  payment_details: draft.payment_details.map(row => ({ label: row.label, value: row.value })),
});

export const toPaymentInstructionPayload = (data: PaymentInstructionsFormData): PaymentInstructionPreviewPayload => ({
  amount: data.amount,
  reference: data.reference,
  deadline: data.deadline || null,
  legal_deadline: data.legal_deadline || null,
  payment_details: data.payment_details,
});
